import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CreditRecord } from '../services/cpeHistoryService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface PreparedLogo {
  dataUrl: string;
  width: number;
  height: number;
}

async function loadImageAsPngDataUrl(url: string): Promise<PreparedLogo> {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const upscaleFactor = 4;
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(sourceWidth * upscaleFactor));
        canvas.height = Math.max(1, Math.round(sourceHeight * upscaleFactor));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        let minX = canvas.width;
        let minY = canvas.height;
        let maxX = -1;
        let maxY = -1;

        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const alpha = data[(y * canvas.width + x) * 4 + 3];
            if (alpha > 0) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (maxX < minX || maxY < minY) {
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            width: canvas.width,
            height: canvas.height,
          });
          return;
        }

        const trimmedWidth = maxX - minX + 1;
        const trimmedHeight = maxY - minY + 1;
        const trimmedCanvas = document.createElement('canvas');
        trimmedCanvas.width = trimmedWidth;
        trimmedCanvas.height = trimmedHeight;
        const trimmedCtx = trimmedCanvas.getContext('2d');

        if (!trimmedCtx) {
          reject(new Error('Trimmed canvas context unavailable'));
          return;
        }

        trimmedCtx.putImageData(ctx.getImageData(minX, minY, trimmedWidth, trimmedHeight), 0, 0);
        resolve({
          dataUrl: trimmedCanvas.toDataURL('image/png'),
          width: trimmedWidth,
          height: trimmedHeight,
        });
      };
      image.onerror = reject;
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function fmtDate(isoDate: string): string {
  return new Date(isoDate + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function fmtDateShort(isoDate: string): string {
  // Numeric format keeps date values on one line in narrow PDF table columns.
  return new Date(isoDate).toLocaleDateString('en-US');
}

// ---------------------------------------------------------------------------
// Brand colors
// ---------------------------------------------------------------------------
const BRAND_GREEN: [number, number, number] = [85, 149, 52];
const BRAND_GREEN_LIGHT: [number, number, number] = [238, 245, 234];
const DARK_TEXT: [number, number, number] = [30, 30, 30];
const MID_GRAY: [number, number, number] = [110, 110, 110];
const LIGHT_ROW: [number, number, number] = [241, 244, 238];

type GroupedCreditRow = {
  key: string;
  base: CreditRecord;
  totalCredits: number;
  entries: Array<{
    id: string;
    creditsEarned: number;
    category?: string;
  }>;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function hasUsableGuid(value?: string): value is string {
  return Boolean(value && value !== EMPTY_GUID);
}

function groupCreditsForDisplay(records: CreditRecord[]): GroupedCreditRow[] {
  const groups = new Map<string, CreditRecord[]>();

  for (const record of records) {
    const groupKey = record.source === 'External'
      ? `external:${hasUsableGuid(record.creditGroupId) ? record.creditGroupId : record.id}`
      : `platform:${hasUsableGuid(record.registrationId) ? record.registrationId : record.id}`;

    const current = groups.get(groupKey);
    if (current) {
      current.push(record);
    } else {
      groups.set(groupKey, [record]);
    }
  }

  const groupedRecords: GroupedCreditRow[] = [];

  for (const [key, recordsInGroup] of groups.entries()) {
    const sortedByDateDesc = [...recordsInGroup].sort(
      (a, b) => new Date(b.dateEarned).getTime() - new Date(a.dateEarned).getTime(),
    );

    const base = sortedByDateDesc[0];
    groupedRecords.push({
      key,
      base,
      totalCredits: recordsInGroup.reduce((sum, current) => sum + current.creditsEarned, 0),
      entries: recordsInGroup.map((entry) => ({
        id: entry.id,
        creditsEarned: entry.creditsEarned,
        category: entry.category,
      })),
    });
  }

  return groupedRecords.sort(
    (a, b) => new Date(b.base.dateEarned).getTime() - new Date(a.base.dateEarned).getTime(),
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export interface CpeReportParams {
  credits: CreditRecord[];
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  userName?: string;
  /**
   * Annotates where the date range came from, e.g. "Tennessee license period".
   * Describes the range's origin only — not a compliance assertion.
   */
  periodContext?: string;
}

export async function generateCpeReport(params: CpeReportParams): Promise<void> {
  const { credits, startDate, endDate, userName, periodContext } = params;
  const groupedCredits = groupCreditsForDisplay(credits);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;

  // ---- Header -------------------------------------------------------------
  doc.setFillColor(...BRAND_GREEN_LIGHT);
  doc.rect(0, 0, pageWidth, 2.5, 'F');

  // Logo
  try {
    const logo = await loadImageAsPngDataUrl('/cpax-academy-logo.svg');
    const maxLogoWidth = 84;
    const targetLogoHeight = 16;
    const aspect = logo.width / logo.height;
    const drawWidth = Math.min(maxLogoWidth, targetLogoHeight * aspect);
    const drawHeight = drawWidth / aspect;
    const logoY = 5;
    doc.addImage(logo.dataUrl, 'PNG', margin, logoY, drawWidth, drawHeight);
  } catch {
    // Logo unavailable — fall back to text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...DARK_TEXT);
    doc.text('CPAx Academy', margin, 14);
  }

  // ---- Report title -------------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...DARK_TEXT);
  doc.text('CPE Reporting', margin, 32);

  // Accent rule under title
  doc.setDrawColor(...BRAND_GREEN);
  doc.setLineWidth(0.7);
  doc.line(margin, 35, pageWidth - margin, 35);

  // ---- Meta block ---------------------------------------------------------
  let y = 44;

  if (userName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...DARK_TEXT);
    doc.text(userName, margin, y);
    y += 7;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...MID_GRAY);

  doc.text(
    `Reporting Period:   ${fmtDate(startDate)}  –  ${fmtDate(endDate)}${periodContext ? `   (${periodContext})` : ''}`,
    margin,
    y,
  );
  y += 6;
  doc.text(
    `Generated:  ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    margin,
    y,
  );
  y += 6;

  const totalHours = credits.reduce((s, c) => s + c.creditsEarned, 0);
  doc.text(
    `Total CPE Hours:   ${totalHours.toFixed(1)}  |  ${groupedCredits.length} activit${groupedCredits.length !== 1 ? 'ies' : 'y'}`,
    margin,
    y,
  );
  y += 10;

  // ---- Credits table ------------------------------------------------------
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Course / Activity', 'Type', 'Credits', 'Category', 'Date']],
    body: groupedCredits.map((group) => {
      const record = group.base;
      const categoryText = group.entries.length > 1
        ? group.entries.map((entry) => `${entry.creditsEarned.toFixed(1)} - ${entry.category ?? 'Uncategorized'}`).join('\n')
        : record.category
          ? `${group.entries[0].creditsEarned.toFixed(1)} - ${record.category}`
          : '—';

      return [
        record.source === 'External' && record.providerName
          ? `${record.courseTitle ?? '—'}\n${record.providerName}`
          : record.courseTitle ?? '—',
        record.source === 'External'
          ? 'External'
          : record.courseType === 1
            ? 'Webinar'
            : 'On-Demand',
        group.totalCredits.toFixed(1),
        categoryText,
        fmtDateShort(record.dateEarned),
      ];
    }),
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.column.index === 2) {
        hookData.cell.styles.fontStyle = 'bold';
      }
    },
    foot: [[
      'Total',
      '',
      totalHours.toFixed(1),
      '',
      '',
    ]],
    showFoot: 'lastPage',
    headStyles: {
      fillColor: BRAND_GREEN,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9.5,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
    },
    footStyles: {
      fillColor: BRAND_GREEN_LIGHT,
      textColor: DARK_TEXT,
      fontStyle: 'bold',
      fontSize: 9.5,
    },
    bodyStyles: {
      fontSize: 9,
      textColor: DARK_TEXT,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
    },
    alternateRowStyles: { fillColor: LIGHT_ROW },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 24, halign: 'center' },
      2: { cellWidth: 22, halign: 'center' },
      3: { cellWidth: 48 },
      4: { cellWidth: 28, halign: 'right' },
    },
    tableLineColor: [220, 220, 230],
    tableLineWidth: 0.2,
  });

  // ---- Per-page footer ----------------------------------------------------
  const pageCount = (doc.internal as unknown as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(210, 210, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MID_GRAY);
    const generatedFromUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : 'N/A';
    doc.text(`CPAx Academy | Generated from: ${generatedFromUrl}`, margin, pageHeight - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  // ---- Save ---------------------------------------------------------------
  const safeName = (userName ?? 'CPE').replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`CPE_Report_${safeName}_${startDate}_to_${endDate}.pdf`);
}
